import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { QueryParams, Routes } from '../../core/enums/module';
import { Analytics, IAnalytics, Locations, backButtonClicked, backToEventRegistrationClicked } from '../../core/services/analytics/module';
import { getRouteBackLink } from '../../utilities/getRouteBackLink';
import { Icons } from '../../icons';
import { safelyExecute } from '../../utilities/safelyExecute';
import { Auth, IAuth } from '../../core/services/auth/auth';
import { getQueryParameters } from '../../utilities/getQueryParameter';
import { Events, IEvents } from '../../core/services/events/events';
import styles from './backButton.module.scss';

type Props = {
	additionalClass?: string;
	getPathnameFunc?: () => string;
	LinkComponent?: typeof Link;
	analytics?: IAnalytics;
	auth?: IAuth;
	eventsService?: IEvents;
	getQueryParametersFunc?: typeof getQueryParameters;
};

export const BackButton: React.FunctionComponent<Props> = ({
	additionalClass,
	getPathnameFunc = () => globalThis.location.pathname,
	LinkComponent = Link,
	analytics = Analytics.Instance(),
	auth = Auth.Instance(),
	eventsService = Events.Instance(),
	getQueryParametersFunc = getQueryParameters
}) => {
	const [backLinkLocation, setBackLinkLocation] = useState<string>('');
	const [eventRegistrationUrl, setEventRegistrationUrl] = useState<string>('');
	const onEmailRoute = (getPathnameFunc() as Routes) === Routes.Email;
	const onWelcomeRoute = (getPathnameFunc() as Routes) === Routes.SignupWelcome;

	useEffect(() => {
		setBackLinkLocation(getRouteBackLink(getPathnameFunc() as Routes) || '');
		safelyExecute(async () => {
			if (!eventRegistrationUrl && !onWelcomeRoute) {
				const urlParams = getQueryParametersFunc();
				const eventId = urlParams.get(QueryParams.RegistrationEventId);
				if (eventId && auth.accessToken) {
					const eventRegistrationResumeResponse = await eventsService.getResumeUrl(eventId, auth.accessToken);
					if (eventRegistrationResumeResponse.wasSuccessful && !eventRegistrationUrl) {
						setEventRegistrationUrl(eventRegistrationResumeResponse.value?.data?.attributes?.url || '');
					}
				}
			}
		});
	});

	const showEventRegistrationLink = !!eventRegistrationUrl && !onWelcomeRoute;

	return (<>
		{showEventRegistrationLink && <>
			<LinkComponent href={eventRegistrationUrl}>
				<a onClick={() => {
					analytics.TrackInteraction(backToEventRegistrationClicked(Locations.Content));
				}} className={`${styles.backLinkHeader} ${styles.eventRegistrationLink} ${onEmailRoute ? styles.lightLink : ''} ${additionalClass ? additionalClass : ''}`}>
					{Icons.LeftArrow}Back to Event Registration
				</a>
			</LinkComponent>
		</>}
		{!!backLinkLocation && <>
			<LinkComponent href={`${backLinkLocation}${location.search}`}>
				<a onClick={() => {
					analytics.TrackInteraction(backButtonClicked(Locations.Content));
				}} className={`${styles.backLinkHeader} ${additionalClass ? additionalClass : ''}`}>
					{Icons.LeftArrow}{showEventRegistrationLink ? 'Previous' : 'Back'}
				</a>
			</LinkComponent>
		</>}
	</>
	);
};
